import React, { useEffect, useRef, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Loading } from 'components/Loading'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchMyTickmillCompaniesAction } from 'store/identity/identity.actions'
import { DomainName, PromoPageFetch } from 'types/Marketing/SGLLinkGenerator.types'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import SGLLinkGeneratorForm from './SGLLinkGeneratorForm'

const SGLLinkGeneratorPage = (): JSX.Element => {
  const crumbs = [{ title: 'Marketing - SGL Link Generator', link: links.sglLinkGenerator.url }]
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const [isLoading, setLoading] = useState<boolean>(false)
  const hasDispatchedFetch = useRef(false)

  const tickmillCompanies = useAppSelector((state) => state.identity.myTickmillCompanies)
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)

  const getAvailablePages = async ({
    entity,
    language,
  }: {
    entity?: string
    language?: string
  }): Promise<{ pagesFetch?: PromoPageFetch; domainsFetch?: DomainName[] }> => {
    if (!entity || !language) return {}
    const pagesFetch = await clientAreaApiClient.getLandingPages({
      params: { companyId: entity, language },
    })
    const domainsFetch = await clientAreaApiClient.getDomainNames({ companyId: entity })
    return { pagesFetch, domainsFetch: [...new Map(domainsFetch.map((v) => [v.name, v])).values()] }
  }

  const generateUrl = async ({
    landingPage,
    domain,
    platform,
    language,
  }: {
    landingPage?: string
    domain?: string
    platform?: string
    language?: string
  }): Promise<{ sglLinkFetchUrl?: string }> => {
    if (!domain || !landingPage) return {}
    const sglLinkFetch = await clientAreaApiClient.getSglLink({
      landingPageId: landingPage,
      domain,
      platform,
      language,
    })
    return { sglLinkFetchUrl: sglLinkFetch.url }
  }

  useEffect(() => {
    if (!hasDispatchedFetch.current && isEmpty(tickmillCompanies)) {
      setLoading(true)
      dispatch(fetchMyTickmillCompaniesAction(identityApiClient))
      setLoading(false)
      hasDispatchedFetch.current = true
    }
  }, [])

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <Loading isLoading={isLoading}>
        <SGLLinkGeneratorForm
          tickmillCompanies={tickmillCompanies}
          getAvailablePages={({ entity, language }: { entity?: string; language?: string }) =>
            getAvailablePages({ entity, language })
          }
          generateUrl={({
            landingPage,
            domain,
            platform,
            language,
          }: {
            landingPage?: string
            domain?: string
            platform?: string
            language?: string
          }) => generateUrl({ landingPage, domain, platform, language })}
        />
      </Loading>
    </React.Fragment>
  )
}

export default SGLLinkGeneratorPage
